import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { useToastContext } from "@finitless/shared/contexts";
import { usePostHog } from "@finitless/shared/hooks";

/**
 * Hook to handle business update operations
 * @param {string|number} businessId - Business ID
 * @returns {Object} Business update functionality
 */
export const useBusinessUpdate = (businessId) => {
  const toast = useToastContext();
  const queryClient = useQueryClient();

  // Fetch business details for editing
  const {
    data: businessData,
    isLoading: isLoadingBusiness,
    error: businessError,
  } = useQuery({
    queryKey: ["business", businessId],
    queryFn: async () => {
      const response = await apiClient.get(`/business/${businessId}`, {
        params: {
          params:
            "id,zones,header,logo,timezone,slug,name,address,cellphone,location,taxes,tax_id,schedule,minimum,price_level,paymethods,delivery_time,pickup_time,cancel_order_after_minutes,order_default_priority",
          mode: "dashboard",
        },
      });
      return response.result;
    },
    enabled: !!businessId && isEndpointEnabled("business", "detail"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update business mutation
  const updateBusinessMutation = useMutation({
    mutationFn: async (updateData) => {
      const response = await apiClient.post(
        `/business/${businessId}`,
        updateData,
      );
      return { response, updateData };
    },
    onSuccess: async ({ response }) => {
      const businessData = response?.error ? null : response.result;
      await queryClient.invalidateQueries({
        queryKey: ["business", businessId],
      });

      if (businessData) {
        queryClient.setQueriesData({ queryKey: ["businesses"] }, (oldData) => {
          if (
            !oldData ||
            !oldData.businesses ||
            !Array.isArray(oldData.businesses)
          ) {
            return oldData;
          }

          const value = {
            ...oldData,
            businesses: oldData.businesses.map((business) =>
              business.id === businessData?.id
                ? { ...business, ...businessData }
                : business,
            ),
          };
          return value;
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["businesses"] });

      toast.success("Business updated successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update business");
    },
  });

  return {
    businessData,
    isLoadingBusiness,
    businessError,
    updateBusiness: updateBusinessMutation.mutateAsync,
    isUpdating: updateBusinessMutation.isPending,
    updateError: updateBusinessMutation.error,
  };
};

/**
 * Hook to manage business payment methods
 * @param {string|number} businessId - Business ID
 * @returns {Object} Payment methods management functionality
 */
export const useBusinessPaymentMethods = (businessId) => {
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const { trackActivation } = usePostHog();

  // Fetch business payment methods
  const {
    data: paymentMethods = [],
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useQuery({
    queryKey: ["business-payment-methods", businessId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/business/${businessId}/paymethods`,
        {
          params: {
            params: "sites,devices,paymethod",
          },
        },
      );
      return response.result || [];
    },
    enabled: !!businessId && isEndpointEnabled("business", "list"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update payment method mutation
  const updatePaymentMethodMutation = useMutation({
    mutationFn: async ({ paymentMethodId, enabled }) => {
      const response = await apiClient.put(
        `/business/${businessId}/paymethods/${paymentMethodId}`,
        { enabled },
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Update the local cache
      queryClient.setQueryData(
        ["business-payment-methods", businessId],
        (oldData) => {
          if (!oldData) return oldData;

          return oldData.map((pm) =>
            pm.id === variables.paymentMethodId
              ? { ...pm, enabled: variables.enabled }
              : pm,
          );
        },
      );

      if (variables?.enabled) {
        // Track payment method update
        trackActivation.paymentMethodAdded({
          business_id: businessId,
          paymethod_id: variables.paymentMethodId,
          enabled: variables.enabled,
        });
      }

      toast.success(
        `Payment method ${
          variables.enabled ? "enabled" : "disabled"
        } successfully`,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update payment method",
      );
    },
  });

  // Add payment method mutation
  const addPaymentMethodMutation = useMutation({
    mutationFn: async ({ paymethod_id, sandbox = false, enabled = true }) => {
      const response = await apiClient.post(
        `/business/${businessId}/paymethods`,
        {
          paymethod_id,
          sandbox,
          enabled,
        },
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch payment methods
      await queryClient.invalidateQueries({
        queryKey: ["business-payment-methods", businessId],
      });

      // Track payment method addition
      trackActivation.paymentMethodAdded({
        business_id: businessId,
        paymethod_id: variables.paymethod_id,
        enabled: variables.enabled,
      });

      toast.success("Payment method added successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to add payment method",
      );
    },
  });

  // Toggle payment method enabled status
  const togglePaymentMethod = useCallback(
    (paymentMethodId, enabled) => {
      updatePaymentMethodMutation.mutate({ paymentMethodId, enabled });
    },
    [updatePaymentMethodMutation],
  );

  return {
    paymentMethods,
    isLoadingPaymentMethods,
    paymentMethodsError,
    togglePaymentMethod,
    isUpdatingPaymentMethod: updatePaymentMethodMutation.isPending,
    addPaymentMethod: addPaymentMethodMutation.mutateAsync,
    isAddingPaymentMethod: addPaymentMethodMutation.isPending,
    refetchPaymentMethods,
  };
};

/**
 * Hook to manage business delivery zones enable/disable
 * @param {string|number} businessId - Business ID
 */
export const useBusinessZones = (businessId) => {
  const toast = useToastContext();
  const queryClient = useQueryClient();

  const updateZoneMutation = useMutation({
    mutationFn: async ({ zoneId, enabled }) => {
      const response = await apiClient.put(
        `/business/${businessId}/deliveryzones/${zoneId}`,
        { enabled },
      );
      return { response, zoneId, enabled };
    },
    onSuccess: async (_data, variables) => {
      // Update cached business data zones enabled state
      queryClient.setQueryData(["business", businessId], (old) => {
        if (!old || !Array.isArray(old.zones)) return old;
        return {
          ...old,
          zones: old.zones.map((z) =>
            z.id === variables.zoneId
              ? { ...z, enabled: variables.enabled }
              : z,
          ),
        };
      });
      toast.success(
        `Zone ${variables.enabled ? "enabled" : "disabled"} successfully`,
      );
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update zone");
    },
  });

  const toggleZone = useCallback(
    (zoneId, enabled) => {
      updateZoneMutation.mutate({ zoneId, enabled });
    },
    [updateZoneMutation],
  );

  const createZoneMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post(
        `/business/${businessId}/deliveryzones`,
        payload,
      );
      return response?.result;
    },
    onSuccess: (newZone) => {
      queryClient.setQueryData(["business", businessId], (old) => {
        if (!old) return old;
        const zones = Array.isArray(old.zones) ? [...old.zones] : [];
        return { ...old, zones: [...zones, newZone] };
      });
      toast.success("Zone created");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create zone");
    },
  });

  const updateZoneDetailsMutation = useMutation({
    mutationFn: async ({ zoneId, payload }) => {
      const response = await apiClient.put(
        `/business/${businessId}/deliveryzones/${zoneId}`,
        payload,
      );
      return { response: response?.result, zoneId, payload };
    },
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(["business", businessId], (old) => {
        if (!old || !Array.isArray(old.zones)) return old;
        return {
          ...old,
          zones: old.zones.map((z) =>
            z.id === variables.zoneId ? { ...z, ...variables.payload } : z,
          ),
        };
      });
      toast.success("Zone updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update zone");
    },
  });

  const deleteZoneMutation = useMutation({
    mutationFn: async (zoneId) => {
      await apiClient.delete(`/business/${businessId}/deliveryzones/${zoneId}`);
      return zoneId;
    },
    onSuccess: (zoneId) => {
      queryClient.setQueryData(["business", businessId], (old) => {
        if (!old || !Array.isArray(old.zones)) return old;
        return {
          ...old,
          zones: old.zones.filter((z) => z.id !== zoneId),
        };
      });
      toast.success("Zone deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete zone");
    },
  });

  const updateZoneBusinessesMutation = useMutation({
    mutationFn: async ({ zoneId, businessIds }) => {
      const response = await apiClient.put(
        `/business/${businessId}/deliveryzones/${zoneId}`,
        {
          businesses: businessIds,
        },
      );
      return response?.result;
    },
    onSuccess: async () => {
      // Ensure fresh data for zones list
      await queryClient.invalidateQueries({
        queryKey: ["business", businessId],
      });
      toast.success("Zone sharing updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update sharing");
    },
  });

  return {
    toggleZone,
    isUpdatingZone: updateZoneMutation.isPending,
    createZone: createZoneMutation.mutateAsync,
    isCreatingZone: createZoneMutation.isPending,
    updateZoneDetails: updateZoneDetailsMutation.mutateAsync,
    isUpdatingZoneDetails: updateZoneDetailsMutation.isPending,
    deleteZone: deleteZoneMutation.mutateAsync,
    isDeletingZone: deleteZoneMutation.isPending,
    updateZoneBusinesses: updateZoneBusinessesMutation.mutateAsync,
    isUpdatingZoneBusinesses: updateZoneBusinessesMutation.isPending,
  };
};

export default {
  useBusinessUpdate,
  useBusinessPaymentMethods,
  useBusinessZones,
};
